import { useState } from "react";
import {
  matchSpokenLinesToCatalog,
  parseSpokenInventoryLines,
  type CatalogMatch,
} from "@/lib/manufacturing/spokenCatalogMatch";
import type { MfgMaterial } from "@/lib/manufacturingClient";
import { newDraftLine, type PoDraftLine } from "@/components/manufacturing/purchaseOrderDraftState";
import { ManufacturingDictatePad } from "./ManufacturingDictatePad";
import { SpokenCatalogMatchList } from "./SpokenCatalogMatchList";

export function PlantMaterialRequestDictate(props: {
  enabled: boolean;
  busy: boolean;
  materials: MfgMaterial[];
  plantId: string;
  onApplyLines: (lines: PoDraftLine[]) => void;
  onApplyNote: (note: string) => void;
}) {
  const [padReset, setPadReset] = useState(0);
  const [heard, setHeard] = useState<CatalogMatch[]>([]);
  const [hint, setHint] = useState<string | null>(null);

  function handleExtract(transcript: string) {
    const spoken = parseSpokenInventoryLines(transcript);
    const catalog = props.materials
      .filter((m) => !props.plantId || m.plant_id === props.plantId)
      .map((m) => ({ id: m.id, name: m.name, code: m.code }));
    const matches = matchSpokenLinesToCatalog(spoken, catalog);
    setHeard(matches);
    const lines: PoDraftLine[] = matches.map((m) => ({
      ...newDraftLine(),
      material_id: m.candidate?.id ?? null,
      material_name: m.candidate?.name ?? m.spoken.name,
      quantity: String(m.spoken.qty),
    }));
    if (lines.length > 0) {
      props.onApplyLines(lines);
      setHint(null);
      setPadReset((n) => n + 1);
      return;
    }
    const leftover = transcript.trim();
    setHint(
      leftover
        ? "Couldn't pick a quantity and item. Saved as a note — purchasing will read it."
        : "Couldn't find a quantity and material name.",
    );
    if (leftover) props.onApplyNote(leftover);
  }

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 dark:border-violet-900 dark:bg-violet-950/20">
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">Dictate</div>
      <p className="mb-2 text-[11px] text-gray-500">
        “Ten OSB” or “a pallet of screws.” Catalog matches become items; anything else goes in the
        note. No vendor needed.
      </p>
      <ManufacturingDictatePad
        disabled={!props.enabled || props.busy}
        placeholder="I need 10 of this item, or a pallet of…"
        extractLabel="Match items"
        resetKey={padReset}
        onComplete={handleExtract}
      />
      <SpokenCatalogMatchList matches={heard} emptyHint={hint} />
    </div>
  );
}
